import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography
} from '@mui/material'
import React, { useContext } from 'react'

import { InvoiceContext } from '../../providers/InvoiceProvider/InvoiceContext'
import { RepairOrderExpanded } from '../../sdk'
import { formatCurrency } from '../../utils'
import { getRepairOrderPricing } from '../../utils/getRepairOrderPricing'

interface Props {
  invoicePDF: boolean
  repairOrder: RepairOrderExpanded
}

export const Totals: React.FC<Props> = ({ invoicePDF, repairOrder }) => {
  const { shopProfile } = useContext(InvoiceContext)
  const hideServices = !!shopProfile?.info?.invoiceByService

  const repairOrderPricing = repairOrder.authorizedPricing!
  const {
    totalLaborPrice,
    totalPartsPrice,
    totalTaxes,
    shopSuppliesFee,
    shopHazmatFee,
    customFees,
    warrantyTotal,
    discountTotal,
    estimateTotal,
    estimateTotalWithSurcharge,
    subtotal
  } = getRepairOrderPricing(repairOrderPricing)

  const displayRowItem = (first: string, second: string, bold?: boolean) => {
    return (
      <TableRow>
        <TableCell sx={{ padding: 0, width: '76%' }}>
          <br />
        </TableCell>
        <TableCell align="right" sx={{ padding: 0, width: '12%' }}>
          <Typography
            variant="body2"
            sx={bold === true ? { fontWeight: 'bold' } : undefined}>
            {first}
          </Typography>
        </TableCell>
        <TableCell align="right" sx={{ padding: 0, width: '12%' }}>
          <Typography
            variant="body2"
            sx={bold === true ? { fontWeight: 'bold' } : undefined}>
            {second}
          </Typography>
        </TableCell>
      </TableRow>
    )
  }
  const displayTableBody = () => {
    if (invoicePDF) {
      return (
        <TableBody>
          {totalLaborPrice !== 0 &&
            displayRowItem('Labor', formatCurrency(totalLaborPrice, true))}
          {totalPartsPrice !== 0 &&
            displayRowItem('Parts', formatCurrency(totalPartsPrice, true))}
          {shopSuppliesFee !== 0 &&
            displayRowItem('Supplies', formatCurrency(shopSuppliesFee, true))}
          {shopHazmatFee !== 0 &&
            displayRowItem('Hazmat', formatCurrency(shopHazmatFee, true))}
          {customFees !== 0 &&
            displayRowItem('Fees', formatCurrency(customFees, true))}
          {warrantyTotal !== 0 &&
            displayRowItem(
              'Warranty',
              formatCurrency(warrantyTotal * -1, true)
            )}
          {discountTotal !== 0 &&
            displayRowItem(
              'Discounts',
              formatCurrency(discountTotal * -1, true)
            )}
          {hideServices &&
            displayRowItem('Subtotal', formatCurrency(subtotal, true))}
          {totalTaxes !== 0 &&
            displayRowItem('Taxes', formatCurrency(totalTaxes, true))}
          {displayRowItem(
            'Grand Total',
            formatCurrency(estimateTotal, true),
            true
          )}
          {estimateTotalWithSurcharge !== estimateTotal &&
            displayRowItem(
              'Total With Credit Card Surcharge',
              formatCurrency(estimateTotalWithSurcharge, true),
              true
            )}
        </TableBody>
      )
    }
    return (
      <TableBody>
        {displayRowItem('Labor', formatCurrency(totalLaborPrice, true))}
        {displayRowItem('Parts', formatCurrency(totalPartsPrice, true))}
        {discountTotal !== 0 &&
          displayRowItem('Discounts', formatCurrency(discountTotal * -1, true))}
        {displayRowItem(
          'Total Before Taxes & Miscellaneous Charges',
          formatCurrency(
            totalLaborPrice + totalPartsPrice - discountTotal,
            true
          )
        )}
      </TableBody>
    )
  }

  return (
    <Stack
      direction="column"
      mx={2}
      sx={{ borderBottom: 1, borderColor: '#424242' }}>
      <TableContainer>
        <Table sx={{ display: 'table', width: '100%' }}>
          {displayTableBody()}
        </Table>
      </TableContainer>
    </Stack>
  )
}
